import {
  hasProgramContentSlideDraftForAnnualKeys,
  hasProgramContentSlideDraftMatchingOwnerCandidates,
  programContentSlidePresenceKey,
} from '../services/programContentSlideService';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { loadLearningContentWorksheetDraft } from './learningContentWorksheetCanvasDraftStorage';
import {
  hasPendingLearningWorksheetFlush,
  hasPendingLearningWorksheetFlushForAnnualKeys,
  resolvePendingWorksheetRefIdForAnnualKeys,
} from './learningContentWorksheetPendingStorage';
import {
  appendPresentationRefOwnerFromLearningText,
  resolvePresentationRefIdFromLearningText,
} from './programContentSlideOwner';
import {
  applyLearningContentOverrides,
  mergeProgramLearningContentFields,
  parseProgramLearningContentOverridesFromEditor,
  parseProgramLearningContentFieldsFromEditor,
  readProgramLearningContentFields,
  serializeProgramLearningContentFieldsForEditor,
} from './programLearningContentFields';
import { mergeProgramLearningTextAttachmentRefs, parseMemoFieldStorageDeep } from './programCellDisplayText';
import { readProgramPresentationListStatsCache } from './programPresentationListStatsCache';
import type { ProgramProgressCell } from '../types/domain';
import { resolveProgramWorksheetRefIdForIndicator } from './programContentWorksheetOwner';
import type { ProgramLearningContentRowLike } from './programLearningContentFields';
import { resolveSyncMirrorLearningTextForIndicators } from './programSyncMirrorIndicators';
import { isLearningAttachmentIndicatorDeletedForKeys } from './learningAttachmentIndicatorStorage';

/** 인디케이터용 draft/cell — draft(빈 문자열 포함)가 LS overlay보다 우선. @see PROGRAM_LEARNING_ATTACHMENT_INVARIANTS */
export function resolveIndicatorDraftAndCellTexts(params: {
  annualKey: string;
  patchAnnualKey?: string;
  draftLearningText?: string | null;
  cellLearningText?: string | null;
  lsOverlayByAnnualKey?: Record<string, string>;
}): { draftLt: string; cellLt: string } {
  const patchKey = params.patchAnnualKey?.trim() || params.annualKey.trim();
  const annualKey = params.annualKey.trim();
  const cellLt = params.cellLearningText ?? '';
  const hasPatchOverlay =
    !!params.lsOverlayByAnnualKey && Object.prototype.hasOwnProperty.call(params.lsOverlayByAnnualKey, patchKey);
  const hasAnnualOverlay =
    !!params.lsOverlayByAnnualKey && Object.prototype.hasOwnProperty.call(params.lsOverlayByAnnualKey, annualKey);
  const overlay = hasPatchOverlay
    ? params.lsOverlayByAnnualKey?.[patchKey] ?? ''
    : hasAnnualOverlay
      ? params.lsOverlayByAnnualKey?.[annualKey] ?? ''
      : undefined;
  /**
   * 우선순위:
   * 1) 명시적 draft(빈 문자열 포함)
   * 2) overlay가 "존재할 때만" overlay
   * 3) 둘 다 없으면 cell 값 (기본값)
   */
  const draftLt =
    params.draftLearningText !== undefined && params.draftLearningText !== null
      ? params.draftLearningText
      : overlay !== undefined
        ? overlay
        : cellLt;
  return { draftLt, cellLt };
}

export function hasLocalLearningTextEdit(
  draftLearningText: string | null | undefined,
  cellLearningText: string | null | undefined
): boolean {
  return (draftLearningText ?? '').trim() !== (cellLearningText ?? '').trim();
}

/**
 * 프로그램 표 인디케이터용 draft — 연간진도표와 같이 LS 오버레이·미저장 편집만 draft로 본다.
 * `draftByAnnualKey` 전체를 항상 넘기면 저장 후에도 draft≠cell로 오판해 DB ref 인디케이터가 꺼질 수 있다.
 */
export function resolveProgramTableIndicatorDraftLearningText(params: {
  annualKey: string;
  patchAnnualKey: string;
  draftLearningText?: string | null;
  cellLearningText?: string | null;
  lsOverlayByAnnualKey?: Record<string, string>;
  hasPendingWorkspaceSave?: boolean;
}): string | undefined {
  const patchKey = params.patchAnnualKey.trim() || params.annualKey.trim();
  const annualKey = params.annualKey.trim();
  const rawDraft = params.draftLearningText;
  const rawCell = params.cellLearningText ?? '';
  const overlay =
    params.lsOverlayByAnnualKey?.[patchKey] ?? params.lsOverlayByAnnualKey?.[annualKey];
  const hasDraftCellDrift =
    rawDraft !== undefined && rawDraft !== null && rawDraft.trim() !== String(rawCell).trim();
  if (params.hasPendingWorkspaceSave || hasDraftCellDrift) {
    return rawDraft ?? undefined;
  }
  return overlay;
}

/**
 * 프로그램 표·연간진도표 공통: 학습내용 셀 표시(본문·note)와 ref 인디케이터가 참조하는 canonical learning_text.
 * presentationRefId·worksheetRefId·note는 draft/cell/LS overlay/동기화 대표 행에서 병합한다.
 */
export function resolveProgramCanonicalLearningTextRaw(params: {
  annualKey: string;
  patchAnnualKey: string;
  draftLearningText?: string | null;
  cellLearningText?: string | null;
  lsOverlayByAnnualKey?: Record<string, string>;
  hasPendingWorkspaceSave?: boolean;
  syncMirrorView?: boolean;
  syncMirrorIndicatorCellsByAnnualKey?: Record<string, ProgramProgressCell>;
  repDraftLearningText?: string | null;
}): string {
  const indicatorDraft = resolveProgramTableIndicatorDraftLearningText({
    annualKey: params.annualKey,
    patchAnnualKey: params.patchAnnualKey,
    draftLearningText: params.draftLearningText,
    cellLearningText: params.cellLearningText,
    lsOverlayByAnnualKey: params.lsOverlayByAnnualKey,
    hasPendingWorkspaceSave: params.hasPendingWorkspaceSave,
  });
  const draftRaw = params.draftLearningText;
  const cellRaw = params.cellLearningText ?? '';
  const draftExplicitClear =
    draftRaw !== undefined &&
    draftRaw !== null &&
    draftRaw.trim() === '' &&
    (params.hasPendingWorkspaceSave || hasLocalLearningTextEdit(draftRaw, cellRaw));
  /** 표에서 칸을 비운 미저장·저장 대기 상태 — cell/overlay 옛 본문으로 폴백하지 않음 */
  if (indicatorDraft === '' || draftExplicitClear) {
    return '';
  }
  const { draftLt, cellLt } = resolveIndicatorDraftAndCellTexts({
    annualKey: params.annualKey,
    patchAnnualKey: params.patchAnnualKey,
    draftLearningText: indicatorDraft,
    cellLearningText: params.cellLearningText,
    lsOverlayByAnnualKey: params.lsOverlayByAnnualKey,
  });
  const patchKey = params.patchAnnualKey.trim() || params.annualKey.trim();
  const rawOverlay =
    params.lsOverlayByAnnualKey?.[patchKey] ?? params.lsOverlayByAnnualKey?.[params.annualKey.trim()];
  let mergedFields = parseProgramLearningContentFieldsFromEditor(cellLt);
  if (draftLt !== undefined && draftLt !== null) {
    const draftOverrides = parseProgramLearningContentOverridesFromEditor(draftLt);
    mergedFields = applyLearningContentOverrides(mergedFields, draftOverrides);
  }
  if (rawOverlay !== undefined) {
    const overlayOverrides = parseProgramLearningContentOverridesFromEditor(rawOverlay);
    mergedFields = applyLearningContentOverrides(mergedFields, overlayOverrides);
  }
  const mergedAttachments = serializeProgramLearningContentFieldsForEditor(mergedFields);
  if (params.syncMirrorView && params.syncMirrorIndicatorCellsByAnnualKey) {
    return resolveSyncMirrorLearningTextForIndicators(
      params.annualKey,
      params.syncMirrorView,
      params.syncMirrorIndicatorCellsByAnnualKey,
      mergedAttachments,
      cellLt,
      params.repDraftLearningText
    );
  }
  if (!mergedAttachments.trim()) {
    if (draftExplicitClear) return '';
    if (
      draftRaw !== undefined &&
      draftRaw !== null &&
      draftRaw.trim() === '' &&
      hasLocalLearningTextEdit(draftRaw, cellRaw)
    ) {
      return '';
    }
  }
  return mergedAttachments || cellLt || draftLt;
}

function resolvePresentationRefFromIndicatorSources(
  ...sources: Array<string | null | undefined>
): string {
  for (const src of sources) {
    const ref = resolvePresentationRefIdFromLearningText(src);
    if (ref) return ref;
  }
  return '';
}

function resolveWorksheetRefFromIndicatorSources(
  ...sources: Array<string | ProgramProgressCell | ProgramLearningContentRowLike | null | undefined>
): string {
  return resolveProgramWorksheetRefIdForIndicator(...sources);
}

/** 활동지 `loadLearningContentWorksheetDraft` / pending annualKeys와 동일 — 로컬 슬라이드 초안 */
function resolvePresentationSlideDraftVisible(
  workspaceId: string,
  candidates: ProgramContentSlideOwnerRef[],
  annualKeys: readonly string[],
  scopeId?: string,
  allowedScopeIds?: readonly string[]
): boolean {
  const sid = workspaceId.trim();
  if (!sid) return false;
  const scopeOpts = {
    scopeId,
    allowedScopeIds:
      allowedScopeIds && allowedScopeIds.length
        ? allowedScopeIds
        : scopeId
          ? [scopeId]
          : undefined,
  };
  if (hasProgramContentSlideDraftForAnnualKeys(sid, annualKeys, scopeOpts)) return true;
  return hasProgramContentSlideDraftMatchingOwnerCandidates(sid, candidates, scopeOpts);
}

export function resolvePresentationIndicatorVisible(params: {
  workspaceId: string;
  annualKey: string;
  patchAnnualKey?: string;
  /** 프로그램 표 스코프 — 있으면 다른 표의 annualCellKey 초안 잔상을 무시 */
  scopeId?: string;
  /**
   * 동기화 대표·미러 등 같은 프레젠을 공유하는 스코프.
   * 없으면 `scopeId`만 허용.
   */
  allowedScopeIds?: readonly string[];
  indicatorLearningText: string;
  draftLearningText: string;
  cellLearningText: string;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  slidePresenceKeys: ReadonlySet<string>;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
}): boolean {
  const sid = params.workspaceId.trim();
  const lt = params.indicatorLearningText;
  const hasLocalEdit = hasLocalLearningTextEdit(params.draftLearningText, params.cellLearningText);
  const patchKey = (params.patchAnnualKey ?? params.annualKey).trim();
  const presentationDeleted = params.deletedHints
    ? isLearningAttachmentIndicatorDeletedForKeys(
        params.deletedHints,
        params.annualKey,
        patchKey,
        'presentation'
      )
    : false;
  const localResolvedRef = hasLocalEdit
    ? resolvePresentationRefFromIndicatorSources(params.draftLearningText)
    : resolvePresentationRefFromIndicatorSources(
        params.draftLearningText,
        lt,
        params.cellLearningText
      );
  if (localResolvedRef) return true;
  if (presentationDeleted) return false;

  const textForResolve = hasLocalEdit
    ? mergeProgramLearningTextAttachmentRefs(
        params.draftLearningText,
        mergeProgramLearningTextAttachmentRefs(lt, params.cellLearningText)
      )
    : lt;

  const candidates = appendPresentationRefOwnerFromLearningText(
    [...params.slideOwnerCandidates],
    textForResolve
  );

  const draftAnnualKeys = [params.annualKey, patchKey];
  if (hasLocalEdit) {
    return resolvePresentationSlideDraftVisible(
      sid,
      candidates,
      draftAnnualKeys,
      params.scopeId,
      params.allowedScopeIds
    );
  }

  const ownerKeys = candidates.map((o) => programContentSlidePresenceKey(o));
  const statsMap = readProgramPresentationListStatsCache(sid, ownerKeys);
  const hasAnyPages = ownerKeys.some((key) => (statsMap.get(key)?.pageCount ?? 0) > 0);
  const hasDb = candidates.some((o) => params.slidePresenceKeys.has(programContentSlidePresenceKey(o)));
  return (
    hasAnyPages ||
    hasDb ||
    resolvePresentationSlideDraftVisible(
      sid,
      candidates,
      draftAnnualKeys,
      params.scopeId,
      params.allowedScopeIds
    )
  );
}

/** 학습내용 셀 학습 메모(note) 인디케이터 — 활동지와 같이 로컬 초안·LS 오버레이를 반영 */
export function resolveLearningMemoNoteIndicatorVisible(params: {
  indicatorLearningText: string;
  draftLearningText: string;
  cellLearningText: string;
}): boolean {
  const hasLocalEdit = hasLocalLearningTextEdit(params.draftLearningText, params.cellLearningText);
  const textForNote = hasLocalEdit ? params.draftLearningText : params.indicatorLearningText;
  if (parseMemoFieldStorageDeep(textForNote).note.trim()) return true;
  if (!hasLocalEdit) {
    return !!parseMemoFieldStorageDeep(params.cellLearningText).note.trim();
  }
  return false;
}

export function resolveWorksheetIndicatorVisible(params: {
  workspaceId: string;
  annualKey: string;
  patchAnnualKey?: string;
  worksheetDraftRowKey: string;
  indicatorLearningText: string;
  draftLearningText: string;
  cellLearningText: string;
  worksheetPresenceRefIds: ReadonlySet<string>;
  programCell?: ProgramProgressCell | null;
  syncMirrorRepCell?: ProgramProgressCell | null;
  lsOverlayLearningText?: string | null;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
}): boolean {
  const sid = params.workspaceId.trim();
  const lt = params.indicatorLearningText;
  const hasLocalEdit = hasLocalLearningTextEdit(params.draftLearningText, params.cellLearningText);
  const draftRowKey = params.worksheetDraftRowKey.trim() || params.annualKey.trim();
  const worksheetDeleted = params.deletedHints
    ? isLearningAttachmentIndicatorDeletedForKeys(
        params.deletedHints,
        params.annualKey,
        params.patchAnnualKey ?? params.annualKey,
        'worksheet'
      )
    : false;
  let refId = hasLocalEdit
    ? resolveWorksheetRefFromIndicatorSources(params.draftLearningText)
    : resolveWorksheetRefFromIndicatorSources(
        params.syncMirrorRepCell,
        params.programCell,
        params.lsOverlayLearningText,
        params.draftLearningText,
        lt,
        params.cellLearningText
      );

  if (refId) return true;
  if (worksheetDeleted) return false;

  if (hasLocalEdit) {
    if (!!sid && hasPendingLearningWorksheetFlushForAnnualKeys(sid, params.annualKey, draftRowKey)) {
      return true;
    }
    return !!loadLearningContentWorksheetDraft(draftRowKey);
  }

  if (!refId && sid) refId = resolvePendingWorksheetRefIdForAnnualKeys(sid, params.annualKey, draftRowKey);
  if (refId) {
    const hasDb = params.worksheetPresenceRefIds.has(refId);
    const hasPending =
      (!!sid && hasPendingLearningWorksheetFlush(sid, refId)) ||
      (!!sid && hasPendingLearningWorksheetFlushForAnnualKeys(sid, params.annualKey, draftRowKey));
    if (hasDb || hasPending) return true;
  }
  return !!loadLearningContentWorksheetDraft(draftRowKey);
}
